/** Overview benchmark settings — mirrors server/utils/vectorBenchmarkSettings.js */

import { themeColor } from './themeColor';

export const BENCHMARK_CATALOG = [
  { id: 'SPY', label: 'S&P 500', shortLabel: 'SPY' },
  { id: 'QQQ', label: 'Nasdaq-100', shortLabel: 'QQQ' },
  { id: 'IEF', label: 'US 7-10Y Treasury', shortLabel: 'IEF' },
  { id: 'GLD', label: 'Gold', shortLabel: 'GLD' },
  { id: 'ES', label: 'E-mini S&P 500', shortLabel: 'ES' },
  { id: 'NQ', label: 'E-mini Nasdaq-100', shortLabel: 'NQ' },
  { id: 'YM', label: 'E-mini Dow Jones', shortLabel: 'YM' },
  { id: 'CL', label: 'Crude Oil', shortLabel: 'CL' },
];

export const BENCHMARK_IDS = new Set(BENCHMARK_CATALOG.map((b) => b.id));

export const DEFAULT_LINE_COLORS = {
  SPY: '#38bdf8',
  QQQ: '#f97316',
  IEF: '#fbbf24',
  GLD: '#facc15',
  ES: '#34d399',
  NQ: '#a78bfa',
  YM: '#f472b6',
  CL: '#fb7185',
};

export const DEFAULT_ENABLED = ['SPY', 'QQQ', 'IEF', 'GLD', 'ES', 'NQ'];
export const DEFAULT_SELECTED = ['SPY'];
export const DEFAULT_HISTORY_START = '2019-01-01';

function isValidIsoDate(value) {
  const s = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  return Number.isFinite(Date.parse(`${s}T00:00:00Z`));
}

function normalizeHexColor(raw, fallback) {
  const s = String(raw || '').trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    const expanded = s.split('').map((c) => c + c).join('');
    return `#${expanded.toLowerCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s.toLowerCase()}`;
  return fallback;
}

function normalizeIdList(raw, { max = 12 } = {}) {
  const list = Array.isArray(raw) ? raw : String(raw || '').split(',');
  const out = [];
  list.forEach((item) => {
    const id = String(item || '').trim().toUpperCase();
    if (!BENCHMARK_IDS.has(id) || out.includes(id)) return;
    out.push(id);
  });
  return out.slice(0, max);
}

export function normalizeBenchmarkSettings(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  let historyStartDate = String(src.historyStartDate || DEFAULT_HISTORY_START).trim();
  if (!isValidIsoDate(historyStartDate) || historyStartDate < '1990-01-01') {
    historyStartDate = DEFAULT_HISTORY_START;
  }

  let enabled = normalizeIdList(src.enabled);
  if (!enabled.length) enabled = [...DEFAULT_ENABLED];

  let defaultSelected = normalizeIdList(src.defaultSelected);
  defaultSelected = defaultSelected.filter((id) => enabled.includes(id));
  if (!defaultSelected.length) {
    defaultSelected = enabled.includes('SPY') ? ['SPY'] : [enabled[0]];
  }

  const colors = {};
  BENCHMARK_CATALOG.forEach(({ id }) => {
    const fallback = themeColor(DEFAULT_LINE_COLORS[id] || '#94a3b8');
    colors[id] = normalizeHexColor(src.colors?.[id], fallback);
  });

  return {
    historyStartDate,
    enabled,
    defaultSelected,
    colors,
  };
}

export function clipSeriesToHistoryStart(series, historyStartDate) {
  const floor = String(historyStartDate || '').trim();
  if (!isValidIsoDate(floor)) return Array.isArray(series) ? series : [];
  return (Array.isArray(series) ? series : []).filter((point) => point?.date && point.date >= floor);
}

export function clipTradesToHistoryStart(trades, historyStartDate) {
  const floor = String(historyStartDate || '').trim();
  if (!isValidIsoDate(floor)) return Array.isArray(trades) ? trades : [];
  return (Array.isArray(trades) ? trades : []).filter((trade) => {
    const date = String(trade?.trade_date || '').slice(0, 10);
    return date && date >= floor;
  });
}

export function clipDailyStatsToHistoryStart(dailyStatsByDate, historyStartDate) {
  const floor = String(historyStartDate || '').trim();
  if (!dailyStatsByDate || !(dailyStatsByDate instanceof Map) || !isValidIsoDate(floor)) {
    return dailyStatsByDate;
  }
  const out = new Map();
  dailyStatsByDate.forEach((value, date) => {
    if (String(date).slice(0, 10) >= floor) out.set(date, value);
  });
  return out;
}

export function isYearOnOrAfterHistoryStart(yearKey, historyStartDate) {
  const floorYear = String(historyStartDate || '').slice(0, 4);
  const year = String(yearKey || '').slice(0, 4);
  if (!/^\d{4}$/.test(floorYear) || !/^\d{4}$/.test(year)) return true;
  return year >= floorYear;
}

/** Latest effective start for overview chart/comparison (user range ∩ admin floor). */
export function resolveOverviewStartIso(userStartIso, historyStartDate) {
  const floor = String(historyStartDate || '').trim();
  const hasFloor = isValidIsoDate(floor);
  const userStart = userStartIso ? String(userStartIso).trim() : null;
  if (!hasFloor) return userStart || null;
  if (!userStart) return floor;
  return userStart >= floor ? userStart : floor;
}

export function resolveMarketOptions(settings) {
  const normalized = normalizeBenchmarkSettings(settings);
  const enabled = new Set(normalized.enabled);
  return BENCHMARK_CATALOG.filter((b) => enabled.has(b.id));
}

export function getBenchmarkLineColor(settings, benchmarkId) {
  const normalized = normalizeBenchmarkSettings(settings);
  const id = String(benchmarkId || '').trim().toUpperCase();
  return themeColor(normalized.colors[id] || DEFAULT_LINE_COLORS[id] || '#94a3b8');
}

export function catalogEntry(id) {
  return BENCHMARK_CATALOG.find((b) => b.id === id) || null;
}
